import React, { Fragment } from 'react'
import Avatar from 'pages/Parent/components/Avatar'
import dayjs from 'dayjs'
import styled from 'styled-components'
import Box from 'shared/components/Box'
import upperFirst from 'lodash/upperFirst'
import { NoteTypeWithStatus } from 'shared/components/Notes/models/note'
import DefaultImage from 'assets/images/logo-small.png'
import { lighten } from 'polished'
import Button from 'shared/components/Button'

type NoteItemProps = {
  note: NoteTypeWithStatus
  user_id: string
  showAvatar?: boolean
  hasRowBtn?: boolean
  rowBtnTitle?: string
  onClickRowBtn?: (row: any) => void
}

function NoteItem(props: NoteItemProps) {
  const { note, user_id, showAvatar = true, hasRowBtn = false, rowBtnTitle = '', onClickRowBtn = () => {} } = props

  return (
    <NoteContainer $hasButton={hasRowBtn}>
      <div className="note-card-wrapper">
        {showAvatar && (
          <Avatar
            imageURL={note?.type === 'System Generated' ? DefaultImage : `${note?.created_by_user?.photo_url || ''}`}
          />
        )}
        <div className={`note-card ${hasRowBtn ? 'with-button' : ''}`}>
          <NoteContentContainer className="note-content-container">
            <NoteCardHeader className="note-card-header">
              <div className="note-card-header--info">
                {note?.type === 'System Generated' && (
                  <div className="note-card-header--user">
                    {note?.is_flagged && 'Member Flagged by System'}
                    {!note?.is_flagged && (
                      <Fragment>
                        Member Unflagged by {`${note?.unflagged_by_user?.full_name}`}
                        <div className="note-card-header--user-type">
                          ({`${upperFirst(note?.unflagged_by_user?.role_name)}`})
                        </div>
                      </Fragment>
                    )}
                  </div>
                )}
                {!['System Generated', 'Manual Flag', 'Manual Warn Flag', 'Program/Event Note'].includes(
                  note.type || '',
                ) && (
                  <div className="note-card-header--user">
                    Note added by {`${note?.created_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      ({`${upperFirst(note?.created_by_user?.role_name)}`})
                    </div>
                  </div>
                )}
                {note?.type === 'Manual Flag' && (
                  <div className="note-card-header--user">
                    {note?.is_flagged && (
                      <Fragment>
                        Member Flagged by {`${note?.created_by_user?.full_name}`}
                        <div className="note-card-header--user-type">
                          ({`${upperFirst(note?.created_by_user?.role_name)}`})
                        </div>
                      </Fragment>
                    )}
                    {!note?.is_flagged && (
                      <Fragment>
                        Member Unflagged by {`${note?.unflagged_by_user?.full_name}`}
                        <div className="note-card-header--user-type">
                          ({`${upperFirst(note?.unflagged_by_user?.role_name)}`})
                        </div>
                      </Fragment>
                    )}
                  </div>
                )}

                {note?.type === 'Manual Warn Flag' && (
                  <div className="note-card-header--user">
                    {note?.is_warned && (
                      <Fragment>
                        Member Reminder by {`${note?.created_by_user?.full_name}`}
                        <div className="note-card-header--user-type">
                          (
                          {`${upperFirst(
                            note?.created_by_user?.role_display_name || note?.created_by_user?.role_name,
                          )}`}
                          )
                        </div>
                      </Fragment>
                    )}
                    {!note?.is_warned && (
                      <Fragment>
                        Member Resolved Reminder by {`${note?.unflagged_by_user?.full_name}`}
                        <div className="note-card-header--user-type">
                          (
                          {`${upperFirst(
                            note?.unflagged_by_user?.role_display_name || note?.unflagged_by_user?.role_name,
                          )}`}
                          )
                        </div>
                      </Fragment>
                    )}
                  </div>
                )}
                {note?.type === 'Program/Event Note' && (
                  <div className="note-card-header--user">
                    Program/Event Note added by {`${note?.created_by_user?.full_name}`}
                    <div className="note-card-header--user-type">
                      ({`${upperFirst(note?.created_by_user?.role_name)}`})
                    </div>
                  </div>
                )}
                <div className="note-card-header--date">
                  {dayjs(note.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}
                </div>
              </div>
            </NoteCardHeader>
            {note?.message && <div className="note-content">{note?.message}</div>}
          </NoteContentContainer>
          {hasRowBtn && (
            <ButtonContainer className="note-button-container">
              <Button
                className="note-button"
                variant="outline-primary"
                onClick={() => {
                  onClickRowBtn(note)
                }}
              >
                {rowBtnTitle}
              </Button>
            </ButtonContainer>
          )}
        </div>
      </div>
    </NoteContainer>
  )
}

export const NoteCardHeader = styled(Box)`
  &,
  .note-card-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .more-options-item-container {
      top: 24px;
    }

    button {
      height: 0;
      padding-bottom: 24px;
    }

    &--info {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
    }

    &--user {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    &--user-type {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: #727272;
      display: inline-block;
      margin-left: 4px;
    }

    &--date {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #727272;
    }
  }
`

const ButtonContainer = styled(Box)`
  // width: 25%;
`
const NoteContentContainer = styled(Box)`
  // width: 25%;
`

const NoteContainer = styled(Box)<{ $hasButton?: boolean }>`
  margin-top: 10px;
  margin-top: ${({ $hasButton }) => ($hasButton ? '0' : '10px')};

  .note-action-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .note-action-select {
    width: 300px;
  }

  .note-action-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    margin-top: 28px;
  }

  .note-card-container {
    width: 772px;
    min-height: 85px;
    margin: 80px auto 85px;
  }

  .note-card-wrapper {
    display: flex;
    flex-direction: row;

    &:not(:first-child) {
      margin-top: 24px;
    }
  }

  .note-card {
    padding: 24px;
    min-height: 50px;
    width: 100%;

    background: #fdfdfd;
    border: 1px solid #f9f9f9;
    border-radius: 8px;

    box-sizing: border-box;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15);
    position: relative;

    &.with-button {
      display: flex;
      padding-top: 10px;
      padding-bottom: 10px;
      align-items: center;

      .note-content-container {
        width: 75%;
      }

      .note-button-container {
        width: 25%;
        .note-button {
          padding: 0 20px;
        }
      }
    }

    &.flagged::after {
      content: '';
      position: absolute;
      top: -1px;
      right: -1px;

      width: 0;
      height: 0;
      border-top: 24px solid ${({ theme }) => theme?.colors?.caution};
      border-left: 24px solid #fdfdfd;
      border-top-right-radius: 8px;
    }
  }

  .avatar-container {
    width: 56px;
    height: 56px;
    min-width: 56px;
    min-height: 56px;

    margin-right: 24px;
    border: 1px solid #dcdcdc;
  }

  .note-content {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 21px;
    color: ${({ theme }) => theme?.colors?.secondary};
    white-space: pre-wrap;
    margin-top: 1.5rem;
  }

  .note-indicator {
    color: ${({ theme }) => theme?.colors?.main};
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    &--attachment {
      text-decoration-line: underline;
      margin-top: 1.5rem;
      width: fit-content;
    }

    &--new-comment {
      color: #188c46;
      background: #d9f2e9;
      width: fit-content;
      border-radius: 4px;
      margin-bottom: 10px;
      padding: 0.25rem 1rem;
      cursor: unset;
    }

    &--comment {
      height: 36px;
      background: ${({ theme }) => lighten(0.6, theme?.colors?.main)};

      border-radius: 200px;
      width: fit-content;
      padding: 0.5rem 1rem;
      margin-top: 1.5rem;
    }

    &--edited {
      display: flex;
      align-items: center;
      color: ${({ theme }) => theme?.colors?.secondary};
      margin-top: 1.2rem;
      width: fit-content;

      svg {
        margin-right: 4px;
      }
    }
  }

  .note-visibilty {
    font-weight: normal;
    font-size: 16px;
    line-height: 21px;
    color: #727272;

    display: flex;
    align-items: center;
    margin: 1rem 0;

    & + * {
      margin-top: 0 !important;
    }

    svg {
      margin-right: 8px;
    }
  }

  .note-additional-detail {
    width: 100%;

    &-container {
      background: rgba(10, 80, 200, 0.06);
      border-radius: 2px;

      margin-top: 1.5rem;
      padding: 1.5rem;
      width: 100%;
      min-height: 95px;

      display: flex;
      align-items: center;

      svg {
        margin-right: 1rem;
      }

      &--comment {
        .note-additional-detail--user {
          color: #727272;
        }

        .note-additional-detail--reason {
          font-weight: normal;
        }
      }
    }

    &--reason {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme?.colors?.secondary};
      margin-top: 4px;
    }

    &--info {
      width: 100%;
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      align-items: center;
    }

    &--user {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    &--user-type {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: #727272;
      display: inline-block;
      margin-left: 4px;
    }

    &--date {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #727272;
    }
  }

  .event-note {
    display: flex;
    flex-direction: row;
    align-items: center;

    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};

    &--key {
      font-weight: bold;
      display: flex;
      align-items: center;

      min-width: 95px;
      margin-right: 24px;

      svg {
        margin-right: 8px;
      }
    }

    &--value {
      font-weight: 500;
    }

    &-status {
      margin-top: 1.5rem;
    }

    &-container {
      display: grid;
      grid-template-rows: auto auto;
      grid-template-columns: repeat(auto-fit, minmax(272px, 1fr));
      row-gap: 0.5rem;

      margin-top: 1.5rem;
    }
  }
`

export default NoteItem
